import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUS_COPY = {
  success: {
    icon: CheckCircle2,
    title: 'Payment Confirmed',
    color: 'text-green-600',
    badge: 'bg-green-50 border-green-200',
    body: "Thanks — your payment has been verified. We'll be in touch with next steps shortly.",
  },
  failed: {
    icon: XCircle,
    title: 'Payment Failed',
    color: 'text-red-600',
    badge: 'bg-red-50 border-red-200',
    body: "Your payment was not completed. No funds were taken — please try again or contact support.",
  },
  abandoned: {
    icon: AlertCircle,
    title: 'Payment Cancelled',
    color: 'text-amber-600',
    badge: 'bg-amber-50 border-amber-200',
    body: 'You exited before completing payment. You can resume any time from the payment page.',
  },
};

const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  // Paystack appends ?reference=... and ?trxref=... to the callback URL
  const reference = params.get('reference') || params.get('trxref');
  const [state, setState] = useState({ loading: true, status: null, payment: null, error: null });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!reference) {
      setState({ loading: false, status: 'failed', payment: null, error: 'No payment reference provided.' });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/paystack/verify/${reference}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || 'Verification failed');
        setState({ loading: false, status: data.status, payment: data.payment, error: null });
      } catch (err) {
        console.error('Verify error:', err);
        setState({ loading: false, status: 'failed', payment: null, error: err.message });
      }
    })();
  }, [reference]);

  const config = STATUS_COPY[state.status] || STATUS_COPY.failed;
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Seo
        title="Payment Status | Winhouse Tutors"
        description="Your Winhouse Tutors payment status."
        path="/payment-success"
        noindex
      />
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-28">
        <Card data-testid="payment-status-card">
          <CardContent className="p-8 sm:p-10 text-center">
            {state.loading ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <Loader2 className="h-10 w-10 animate-spin text-gray-400" />
                <p className="text-gray-600">Verifying your payment with Paystack…</p>
              </div>
            ) : (
              <>
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full border ${config.badge} mb-4`}>
                  <Icon className={`h-9 w-9 ${config.color}`} />
                </div>
                <h1
                  className="text-2xl sm:text-3xl font-bold text-gray-900"
                  data-testid={`payment-status-${state.status}`}
                >
                  {config.title}
                </h1>
                <p className="mt-3 text-gray-600">{config.body}</p>

                {state.payment && (
                  <div className="mt-6 text-left bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-gray-500">Reference:</span><code className="font-mono text-xs">{state.payment.paystack_reference}</code></div>
                    <div className="flex justify-between"><span className="text-gray-500">Amount:</span><span className="font-semibold">{state.payment.amount} {state.payment.currency}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Type:</span><span>{state.payment.payment_type}</span></div>
                    {state.payment.verified_at && (
                      <div className="flex justify-between"><span className="text-gray-500">Verified:</span><span>{new Date(state.payment.verified_at).toLocaleString()}</span></div>
                    )}
                  </div>
                )}

                {state.error && <p className="mt-4 text-sm text-red-600">{state.error}</p>}

                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <Link to="/"><Button variant="outline">Return Home</Button></Link>
                  {state.status !== 'success' && (
                    <Link to="/pay"><Button className="btn-teal">Try Again</Button></Link>
                  )}
                  {state.status === 'success' && (
                    <Link to="/get-started"><Button className="btn-teal">Continue Onboarding</Button></Link>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
